import { isSupportedConvertImageFile } from '../convert/convertImageEngine';
import { encodeCanvas, legacyMetaFor, resolveOutputFormat } from '../shared/imageOutputFormats';

export { formatFileSize } from '../compress/compressImageEngine';
export const isSupportedResizeImageFile = isSupportedConvertImageFile;

async function prepareFile(file) {
    const name = file.name.toLowerCase();
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (isHeic) {
        const heic2any = (await import('heic2any')).default;
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        const base = file.name.replace(/\.(heic|heif)$/i, '');
        return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    }
    return file;
}

export async function readImageDimensions(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function loadImageElement(file) {
    const prepared = await prepareFile(file);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function outputMeta(file) {
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    if (type === 'image/png' || name.endsWith('.png')) {
        return { mime: 'image/png', ext: 'png', quality: null, alpha: true };
    }
    if (type === 'image/webp' || name.endsWith('.webp')) {
        return { mime: 'image/webp', ext: 'webp', quality: 0.92, alpha: true };
    }
    if (type === 'image/gif' || name.endsWith('.gif')) {
        return { mime: 'image/png', ext: 'png', quality: null, alpha: true };
    }
    if (type === 'image/bmp' || name.endsWith('.bmp')) {
        return { mime: 'image/png', ext: 'png', quality: null, alpha: false };
    }
    return { mime: 'image/jpeg', ext: 'jpg', quality: 0.92, alpha: false };
}

function resolveTargetSize(naturalWidth, naturalHeight, options) {
    const { mode, width, height, percent, lockAspect } = options;

    if (mode === 'percent') {
        const scale = Math.max(1, percent) / 100;
        return {
            width: Math.max(1, Math.round(naturalWidth * scale)),
            height: Math.max(1, Math.round(naturalHeight * scale)),
        };
    }

    let targetW = Math.max(1, Math.round(width));
    let targetH = Math.max(1, Math.round(height));

    if (lockAspect && naturalWidth > 0 && naturalHeight > 0) {
        const ratio = naturalWidth / naturalHeight;
        const widthDriven = options.dimensionDriver === 'width';
        if (widthDriven) {
            targetH = Math.max(1, Math.round(targetW / ratio));
        } else {
            targetW = Math.max(1, Math.round(targetH * ratio));
        }
    }

    return { width: targetW, height: targetH };
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), mime, quality ?? undefined);
    });
}

function outputName(file, ext) {
    const base = file.name.replace(/\.[^.]+$/, '');
    return `${base}-resized.${ext}`;
}

/**
 * Resize an image in the browser.
 * @param {File} file
 * @param {{ mode: 'pixels'|'percent', width?: number, height?: number, percent?: number, lockAspect?: boolean, dimensionDriver?: 'width'|'height' }} options
 */
export async function resizeImageFile(file, options) {
    const img = await loadImageElement(file);
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const { width: targetW, height: targetH } = resolveTargetSize(naturalWidth, naturalHeight, options);
    // The picked output format; "original" keeps the file's own (see shared/imageOutputFormats).
    const meta = legacyMetaFor(resolveOutputFormat(options.format, file));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d', { alpha: meta.alpha });

    if (!meta.alpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, targetW, targetH);
    }

    ctx.drawImage(img, 0, 0, targetW, targetH);

    // Through the shared encoder: AVIF needs WebAssembly (Chrome's canvas cannot
    // write it), and the extension follows what was actually written.
    const { blob, ext } = await encodeCanvas(canvas, resolveOutputFormat(options.format, file), {
        quality: meta.quality ?? 0.92,
    });

    return {
        name: outputName(file, ext),
        blob,
        originalWidth: naturalWidth,
        originalHeight: naturalHeight,
        width: targetW,
        height: targetH,
        originalSize: file.size,
        resizedSize: blob.size,
    };
}

export function isResizeConfigValid(mode, { width, height, percent }) {
    if (mode === 'percent') {
        return percent >= 1 && percent <= 500;
    }
    return width >= 1 && width <= 10000 && height >= 1 && height <= 10000;
}
